import { TextingIndicator } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  formatDateDisplayLocal,
  formatDateTimeDisplayLocal,
  formatPhoneNumber,
  getRelativeDate,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import moment from 'moment';
import { intersection } from 'ramda';
import React from 'react';
import {
  appendFilterTextToCSV,
  excelEscapeString,
} from '../../../utils/csv-helpers';
import { download } from '../../../utils/downloadFile';
import { getUrlWithSelectedPropertyId } from '../../../utils/navigation-helpers';
import actionsMenuMessages from '../../ManageProspects/ActionsMenu/messages';
import { UNIT_ASSIGNED_OPTIONS } from './ApplicationsTable_V2.constants';
import messages from './messages';
import { TAB_KEYS } from '../../ResidentProfile/constants';
import { TEXTING_FILTER_OPTIONS } from '../../../constants/texting';
import { formatTextingCounts } from '../../../utils/texting';
import { isNextActivityLate } from '../../../utils/date-helpers';
import {
  accessibilityTypeCell,
  adaHeaders,
  formatADA,
  formatAccessibilityTypes,
} from '../../../utils/ada-helpers';

export const addProspect = (field: string, prospect: any) => {
  return Object.assign({}, prospect[field], { prospect });
};

export const extractLeadSourcesString = (application) => {
  const contact = application?.contact;
  const prospect = application?.prospect;

  // Check if contact exists and has leadSources
  if (
    contact &&
    contact.leadSources &&
    Array.isArray(contact.leadSources) &&
    contact.leadSources.length > 0
  ) {
    // Sort by createdAt (chronological order - earliest first)
    const sortedLeadSources = contact.leadSources
      .filter((source) => source?.referralType?.name) // Filter out invalid entries
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map((source) => source.referralType.name);

    if (sortedLeadSources.length > 0) {
      return sortedLeadSources.join(', ');
    }
  }

  // Fallback to prospect referralType if no contact leadSources
  if (prospect?.referralType?.name) {
    return prospect.referralType.name;
  }

  return 'N/A';
};

/**
 * Extracts unique lead source names from applications for filter options
 * @param {Array} applications - Array of application objects
 * @returns {Set} Set of unique lead source names
 */
export const extractUniqueLeadSources = (applications) => {
  const leadSourceOptions = new Set();

  applications.forEach((application) => {
    const contact = application?.contact;
    const prospect = application?.prospect;

    // Extract from contact.leadSources (primary source of truth)
    if (contact?.leadSources) {
      contact.leadSources.forEach((leadSource) => {
        if (leadSource.referralType?.name) {
          leadSourceOptions.add(leadSource.referralType.name);
        }
      });
    }

    // Fallback to prospect referralType if no contact leadSources
    if (
      (!contact?.leadSources || contact.leadSources.length === 0) &&
      prospect?.referralType?.name
    ) {
      leadSourceOptions.add(prospect.referralType.name);
    }
  });

  return leadSourceOptions;
};

/**
 * Custom filter function for lead sources that handles comma-separated values
 * @param {string[]} filterByArr - Array of selected lead source names from filter (already lowercase)
 * @param {string} filterValue - Comma-separated string of all lead sources for an application
 * @returns {boolean} True if any selected lead source is found in the application's lead sources
 */
export const leadSourceFilterFn = (filterByArr, filterValue) => {
  if (!filterByArr || filterByArr.length === 0) {
    return true; // No filter applied, show all
  }

  if (!filterValue || filterValue === 'N/A') {
    return false; // No lead sources to match against
  }

  // Split the comma-separated string into individual lead sources and convert to lowercase
  const applicationLeadSources = filterValue
    .split(', ')
    .map((source) => source.trim().toLowerCase());

  // Check if any selected lead source matches any of the application's lead sources
  // Note: filterByArr is already lowercase from the table component
  return filterByArr.some((selected) =>
    applicationLeadSources.includes(selected),
  );
};

export const buildHeaders = ({
  intl,
  generalAffordableProgram,
  hudProgram,
  applicationStatusOptions,
  assignedToOptions,
  showTextingColumn,
  showLeadSourcesColumn,
  leadSourceOptions,
  showADAColumns,
}) => [
  {
    id: 'applicationCode',
    label: intl.formatMessage(messages.applicationCode),
    sortable: true,
    dataType: 'number',
  },
  {
    id: 'applicants',
    label: intl.formatMessage(messages.applicants),
    searchable: true,
    sortable: true,
    dataType: 'string',
  },
  ...(showTextingColumn
    ? [
        {
          id: 'texting',
          label: intl.formatMessage(messages.texting),
          sortable: true,
          searchable: false,
          dataType: 'number',
          multiselect: true,
          filterOptions: TEXTING_FILTER_OPTIONS,
        },
      ]
    : []),
  ...(showLeadSourcesColumn
    ? [
        {
          id: 'leadSources',
          label: intl.formatMessage(messages.leadSources),
          searchable: true,
          sortable: true,
          dataType: 'string',
          multiselect: true,
          filterOptions: leadSourceOptions,
        },
      ]
    : []),
  {
    id: 'applicationStatusId',
    label: intl.formatMessage(messages.status),
    sortable: true,
    filterOptions: applicationStatusOptions,
    dataType: 'string',
    multiselect: true,
  },
  // General Affordable Program
  ...(generalAffordableProgram
    ? [
        {
          id: 'complianceApproval',
          label: intl.formatMessage(messages.complianceApproval),
          sortable: true,
          dataType: 'string',
        },
        {
          id: 'complianceDocument',
          label: intl.formatMessage(messages.complianceDocument),
          sortable: true,
          dataType: 'string',
        },
      ]
    : []),
  // HUD Program
  ...(hudProgram
    ? [
        {
          id: 'complianceApprovalHUD',
          label: intl.formatMessage(messages.complianceApprovalHUD),
          sortable: true,
          dataType: 'string',
        },
        {
          id: 'complianceDocumentHUD',
          label: intl.formatMessage(messages.complianceDocumentHUD),
          sortable: true,
          dataType: 'string',
        },
      ]
    : []),
  {
    id: 'unitNumber',
    label: intl.formatMessage(messages.assignedUnitNumber),
    sortable: true,
    searchable: true,
    filterOptions: UNIT_ASSIGNED_OPTIONS,
    dataType: 'string',
  },
  ...(showADAColumns
    ? adaHeaders({
        adaLabel: intl.formatMessage(messages.ada),
        accessibilityTypeLabel: intl.formatMessage(messages.accessibilityType),
      })
    : []),
  {
    id: 'buildingNumber',
    label: intl.formatMessage(messages.buildingNumber),
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'bedsBaths',
    label: intl.formatMessage(messages.bedsBaths),
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'phoneNumber',
    label: intl.formatMessage(messages.phoneNumber),
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'email',
    label: intl.formatMessage(messages.email),
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'lastActivity',
    label: intl.formatMessage(messages.lastActivity),
    sortable: true,
    dataType: 'date',
  },
  {
    id: 'nextActivity',
    label: intl.formatMessage(messages.nextActivity),
    sortable: true,
    dataType: 'date',
  },
  {
    id: 'desiredMoveInDate',
    label: intl.formatMessage(messages.desiredMoveInDate),
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'scheduledMoveInDate',
    label: intl.formatMessage(messages.scheduleMoveInDate),
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'notes',
    label: intl.formatMessage(messages.applicantNotes),
    sortable: true,
  },
  {
    id: 'applicantConversionDate',
    label: intl.formatMessage(messages.applicantConversionDate),
    sortable: true,
    dataType: 'date',
  },
  {
    id: 'waitlistStartTime',
    label: intl.formatMessage(messages.waitlistDateTime),
    sortable: true,
    dataType: 'date',
  },
  {
    id: 'waitlistNotes',
    label: intl.formatMessage(messages.waitlistNotes),
    sortable: true,
    dataType: 'string',
  },
  {
    id: 'assignedTo',
    label: intl.formatMessage(messages.assignedTo),
    sortable: true,
    searchable: true,
    multiselect: true,
    dataType: 'string',
    filterOptions: assignedToOptions,
  },
  {
    id: 'actions',
    label: intl.formatMessage(messages.actions),
  },
];

export const buildRows = ({
  mappedApplications,
  generalAffordableProgram,
  hudProgram,
  locale,
  onCreateActivity,
  onEditActivity,
  hasActivityReadUpdateDeletePermission,
  hasActivityCreatePermission,
  intl,
}) => {
  return mappedApplications.map((application) => {
    const {
      id,
      applicants,
      applicationCode,
      applicationStatus,
      bedsBaths,
      buildingNumber,
      assigneeName,
      createdAt,
      unitNumber,
      prospect,
      desiredMoveInStartDate,
      moveInDate,
      emailAddress,
      phoneNumber,
      complianceApproval,
      complianceDocument,
      complianceApprovalHUD,
      complianceDocumentHUD,
      notes,
      unitId,
      wlStartTime,
      waitlistNotes,
      texting,
      isADA,
      accessibilityTypes,
    } = application;

    return {
      id,
      applicationCode: {
        variant: 'number',
        value: applicationCode,
      },
      applicants: {
        variant: 'link',
        value: applicants,
        to: getUrlWithSelectedPropertyId(`/application/${application.id}`),
        target: '_blank',
      },
      texting: {
        variant: texting.TOTAL > 0 ? 'link' : undefined,
        value: <TextingIndicator {...texting} />,
        to:
          texting.TOTAL > 0
            ? getUrlWithSelectedPropertyId(
                `/application/${application.id}?tab=${TAB_KEYS.TEXTING}`,
              )
            : undefined,
      },
      leadSources: extractLeadSourcesString(application),
      applicationStatusId: applicationStatus?.translations[locale],
      ...(generalAffordableProgram && {
        complianceApproval: {
          variant: 'complianceApprovalStatus',
          value: complianceApproval,
        },
        complianceDocument: {
          variant: 'description',
          value: complianceDocument,
        },
      }),
      ...(hudProgram && {
        complianceApprovalHUD: {
          variant: 'description',
          value: complianceApprovalHUD,
        },
        complianceDocumentHUD: {
          variant: 'description',
          value: complianceDocumentHUD,
        },
      }),
      unitNumber: unitId
        ? {
            variant: 'link',
            value: unitNumber,
            to: getUrlWithSelectedPropertyId(`/unit/${unitId}`),
          }
        : undefined,
      ada: formatADA(isADA),
      accessibilityType: accessibilityTypeCell(
        formatAccessibilityTypes(isADA, accessibilityTypes),
      ),
      bedsBaths,
      buildingNumber,
      phoneNumber: {
        variant: 'phoneNumber',
        value: phoneNumber,
      },
      email: {
        variant: 'email',
        value: emailAddress,
      },
      lastActivity: {
        variant: 'button',
        disabled: !hasActivityReadUpdateDeletePermission,
        onClick: () =>
          onEditActivity(addProspect('lastAct', prospect), application.id),
        value: prospect?.lastAct?.startTime
          ? getRelativeDate(prospect.lastAct.startTime)
          : null,
      },
      nextActivity: application.nextActivityLoading
        ? {
            variant: 'custom',
            value: <Spinner small />,
          }
        : application.nextActivityError
        ? {
            variant: 'description',
            value: 'Unavailable',
          }
        : {
            variant: 'button',
            disabled: !hasActivityReadUpdateDeletePermission,
            onClick: () =>
              onEditActivity(addProspect('nextAct', prospect), application.id),
            value: prospect?.nextAct?.startTime
              ? (() => {
                  const localDate = formatDateTimeDisplayLocal(
                    prospect.nextAct.startTime,
                    false,
                  );
                  const relativeDate = getRelativeDate(localDate);
                  return relativeDate;
                })()
              : null,
            ...(isNextActivityLate(prospect.nextAct) && {
              ButtonProps: {
                sx: {
                  color: 'error.main',
                },
              },
            }),
          },
      desiredMoveInDate: { variant: 'date', value: desiredMoveInStartDate },
      scheduledMoveInDate: { variant: 'date', value: moveInDate },
      notes: { variant: 'description', value: notes },
      applicantConversionDate: { variant: 'date', value: createdAt },
      waitlistStartTime: { variant: 'dateTime', value: wlStartTime },
      waitlistNotes: {
        variant: 'description',
        value: waitlistNotes,
      },
      assignedTo: assigneeName,
      actions: {
        variant: 'menu',
        iconName: 'BoltIcon',
        color: 'primary',
        options: hasActivityCreatePermission
          ? [
              {
                text: intl.formatMessage(actionsMenuMessages.createActivity),
                value: 'createActivity',
                onClick: () => onCreateActivity(prospect, application.id),
              },
            ]
          : null,
      },
    };
  });
};

export const downloadCSV = ({
  mappedApplications,
  permissions,
  locale,
  affordablePrograms,
  hasAnyFilters,
  headers = [],
  showTextingColumn,
  showLeadSourcesColumn,
  showADAColumns,
}) => {
  const csvHeaders = headers
    .filter(
      ({ id }) =>
        !['waitlistStartTime', 'waitlistNotes', 'actions'].includes(id),
    )
    ?.map(({ label }) => label)
    .join(',');
  // Resolved once, outside the row loop: keeps the per-row callback branch-free
  // and positionally in sync with the ADA headers in buildHeaders, which sit
  // immediately after Assigned Unit #.
  const buildADAFields = showADAColumns
    ? ({ isADA, accessibilityTypes }) => [
        formatADA(isADA) ?? '',
        `"${formatAccessibilityTypes(isADA, accessibilityTypes) ?? ''}"`,
      ]
    : () => [];

  const rows = mappedApplications.reduce((prev, application) => {
    const {
      applicants,
      applicationCode,
      applicationStatus,
      assigneeName,
      complianceApproval = '---',
      complianceDocument = '---',
      complianceApprovalHUD = '---',
      complianceDocumentHUD = '---',
      buildingNumber = 'N/A',
      bedsBaths = 'N/A',
      unitNumber = '',
      prospect,
      desiredMoveInStartDate,
      desiredMoveInEndDate,
      moveInDate,
      emailAddress,
      phoneNumber,
      createdAt,
      notes = '---',
      texting,
    } = application;

    const status = applicationStatus.translations[locale];
    const mappedEmailAddress = emailAddress || '---';
    const userScopes = permissions.map((permission) => permission.scope);
    const hasPermissions =
      intersection(
        ['activity-read', 'activity-update', 'activity-delete'],
        userScopes,
      ).length > 0;
    const lastActivity =
      prospect?.lastAct?.startTime && hasPermissions
        ? formatDateDisplayLocal(prospect.lastAct.startTime)
        : '---';
    const nextActivity =
      prospect?.nextAct?.startTime && hasPermissions
        ? formatDateDisplayLocal(prospect.nextAct.startTime)
        : '---';
    let desiredMoveInDate = desiredMoveInStartDate
      ? formatDateDisplayLocal(desiredMoveInStartDate)
      : '---';
    desiredMoveInDate !== 'Invalid date'
      ? (desiredMoveInDate +=
          desiredMoveInEndDate && desiredMoveInStartDate
            ? ` - ${moment(desiredMoveInEndDate).format('MM/DD/YYYY')}`
            : '')
      : (desiredMoveInDate = '---');

    const scheduledMoveInDate =
      moveInDate !== null ? moment(moveInDate).format('MM/DD/YYYY') : '---';

    const { hudProgram, generalAffordableProgram } = affordablePrograms;
    const generalAffordableData = generalAffordableProgram
      ? [complianceApproval, complianceDocument]
      : [];
    const hudProgramData = hudProgram
      ? [complianceApprovalHUD, complianceDocumentHUD]
      : [];
    const textingIndicatorsData = showTextingColumn
      ? [`"${formatTextingCounts(texting)}"`]
      : [];

    const leadSourcesData = showLeadSourcesColumn
      ? [`"${extractLeadSourcesString(application)}"`]
      : [];

    const adaData = buildADAFields(application);

    const safeUnitNumber = unitNumber ?? '';
    const safeBedsBaths = bedsBaths ?? '';
    const finalFields = [
      applicationCode,
      `"${applicants}"`,
      ...textingIndicatorsData,
      ...leadSourcesData,
      status,
      ...generalAffordableData,
      ...hudProgramData,
      excelEscapeString(safeUnitNumber),
      ...adaData,
      buildingNumber,
      excelEscapeString(safeBedsBaths),
      formatPhoneNumber(phoneNumber),
      mappedEmailAddress,
      lastActivity,
      nextActivity,
      desiredMoveInDate,
      scheduledMoveInDate,
      `"${notes}"`,
      moment(createdAt).format('MM/DD/YYYY'),
      assigneeName,
    ];
    prev.push(finalFields.join(','));
    return prev;
  }, []);

  const csv = appendFilterTextToCSV({
    headers: csvHeaders,
    rows,
    hasAnyFilters,
  });
  download(csv, 'ManageApplication.csv', 'text/csv;charset=utf-8');
};
